import type { SVGProps } from "react";

/**
 * Les quelques icônes de l'admin, dessinées à la main.
 *
 * HeroUI v3 ne fournit pas de jeu d'icônes et le projet n'en installe aucun :
 * pour une poignée de glyphes, une dépendance de plus (et son tree-shaking à
 * surveiller) coûterait plus cher que ces `path`. Elles vivent dans
 * `src/components/` parce qu'une icône n'appartient par nature à aucun écran —
 * la première réutilisation arrive toujours.
 *
 * Conventions : grille 24, `stroke="currentColor"`, aucune taille en dur — la
 * couleur vient du parent et la taille d'une classe (`size-4`). Elles sont
 * décoratives (`aria-hidden`) : c'est au contrôle qui les porte de nommer
 * l'action.
 */
type IconProps = SVGProps<SVGSVGElement>;

function Icon({ children, ...props }: IconProps) {
  return (
    <svg
      aria-hidden="true"
      fill="none"
      focusable="false"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.5}
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      {children}
    </svg>
  );
}

export function SunIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M19.07 4.93l-1.41 1.41M6.34 17.66l-1.41 1.41" />
    </Icon>
  );
}

export function MoonIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" />
    </Icon>
  );
}

export function MailIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect height="16" rx="2" width="20" x="2" y="4" />
      <path d="m2 7 10 6 10-6" />
    </Icon>
  );
}

export function LockIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect height="10" rx="2" width="18" x="3" y="11" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </Icon>
  );
}

export function EyeIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </Icon>
  );
}

export function EyeOffIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M10.7 5.1A10.7 10.7 0 0 1 12 5c6.4 0 10 7 10 7a18.5 18.5 0 0 1-2.6 3.7M6.6 6.6A18.6 18.6 0 0 0 2 12s3.6 7 10 7a10.4 10.4 0 0 0 4.4-.9" />
      <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
      <path d="m2 2 20 20" />
    </Icon>
  );
}

/* ── Navigation ─────────────────────────────────────────────────────────── */

export function DashboardIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect height="7" rx="1.5" width="7" x="3" y="3" />
      <rect height="7" rx="1.5" width="7" x="14" y="3" />
      <rect height="7" rx="1.5" width="7" x="14" y="14" />
      <rect height="7" rx="1.5" width="7" x="3" y="14" />
    </Icon>
  );
}

export function ReportsIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4 21V4M4 4h10.5l-1.2 3.5L14.5 11H4" />
    </Icon>
  );
}

export function EventsIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect height="17" rx="2" width="18" x="3" y="4" />
      <path d="M3 10h18M8 2v4M16 2v4" />
    </Icon>
  );
}

export function AccountsIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M16 20v-1.5a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4V20" />
      <circle cx="9" cy="7" r="3.5" />
      <path d="M22 20v-1.5a4 4 0 0 0-3-3.87M16.5 3.6a4 4 0 0 1 0 6.8" />
    </Icon>
  );
}

/**
 * Le fil d'actualité : une pile d'images.
 *
 * Volontairement distincte de `ReportsIcon` (le fanion) — les deux écrans sont
 * dans la même section « Modération », et deux glyphes proches y seraient
 * indiscernables une fois la barre repliée, où l'icône est tout ce qui reste.
 */
export function PostsIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect height="13" rx="2" width="15" x="6" y="3" />
      <path d="M6 8H4a1 1 0 0 0-1 1v10a2 2 0 0 0 2 2h10a1 1 0 0 0 1-1v-4" />
      <circle cx="11" cy="8" r="1.5" />
      <path d="m8 15 3.5-3.5L15 15l2-2 4 4" />
    </Icon>
  );
}

export function SettingsIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.6 1.6 0 0 0 .32 1.77l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.6 1.6 0 0 0-1.77-.32 1.6 1.6 0 0 0-1 1.47V21a2 2 0 1 1-4 0v-.1a1.6 1.6 0 0 0-1.05-1.47 1.6 1.6 0 0 0-1.77.32l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.6 1.6 0 0 0 .32-1.77 1.6 1.6 0 0 0-1.47-1H3a2 2 0 1 1 0-4h.1a1.6 1.6 0 0 0 1.47-1.05 1.6 1.6 0 0 0-.32-1.77l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.6 1.6 0 0 0 1.77.32H9a1.6 1.6 0 0 0 1-1.47V3a2 2 0 1 1 4 0v.1a1.6 1.6 0 0 0 1 1.47 1.6 1.6 0 0 0 1.77-.32l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.6 1.6 0 0 0-.32 1.77V9a1.6 1.6 0 0 0 1.47 1H21a2 2 0 1 1 0 4h-.1a1.6 1.6 0 0 0-1.47 1Z" />
    </Icon>
  );
}

/* ── Contrôles de la coque ──────────────────────────────────────────────── */

export function MenuIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4 6h16M4 12h16M4 18h16" />
    </Icon>
  );
}

export function ChevronLeftIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="m15 5-7 7 7 7" />
    </Icon>
  );
}

export function LogOutIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <path d="m16 17 5-5-5-5M21 12H9" />
    </Icon>
  );
}

export function GlobeIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="9" />
      {/* Un méridien et l'équateur : c'est ce qui distingue un globe d'un
          simple cercle une fois réduit à 16 px. */}
      <path d="M3 12h18M12 3a15 15 0 0 1 0 18 15 15 0 0 1 0-18Z" />
    </Icon>
  );
}

/** Hexagone plein — la forme de l'icône de l'app, utilisée comme puce. */
export function HexagonIcon(props: IconProps) {
  return (
    <Icon fill="currentColor" stroke="none" {...props}>
      <path d="M12 2.2 20.5 7v10L12 21.8 3.5 17V7Z" />
    </Icon>
  );
}
